<script>
import { DpIconCaret } from '@dp-ui-kit/icons';
import { Comment, h, resolveComponent } from 'vue';

import DpNavHeader from './DpNavHeader.vue';

export default {
  name: 'DpNavItem',

  compatConfig: {
    INSTANCE_LISTENERS: false,
  },

  components: {
    DpNavHeader,
    DpIconCaret,
  },

  props: {
    text: {
      required: false,
      type: String,
      default: '',
    },
    href: {
      required: false,
      type: String,
      default: '#',
    },
    to: {
      required: false,
      type: [String, Object],
      default: null,
    },
    active: {
      required: false,
      type: Boolean,
      default: false,
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false,
    },
    iconOffset: {
      required: false,
      type: Boolean,
      default: false,
    },
    subNavAlwaysOpened: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    subNavIsOpen: false,
    hasSubNav: false,
  }),

  computed: {
    linkTag() {
      if (this.to && !this.hasSubNav) {
        return resolveComponent('router-link');
      }

      return 'a';
    },

    shouldPreventDefault() {
      const { to, href, disabled } = this;

      if (disabled) {
        return true;
      }

      if (!to && href === '#') {
        return true;
      }

      return false;
    },
  },

  methods: {
    hasValidSlot(slot) {
      if (!slot) {
        return false;
      }

      const slotContent = slot();
      const hasContent = slotContent.findIndex((o) => o.type !== Comment) !== -1;

      if (!hasContent) {
        return false;
      }

      return true;
    },

    navItemLeftContent(navItemLeftSlot) {
      if (!this.hasValidSlot(navItemLeftSlot)) {
        return null;
      }

      return h(
        'div',
        {
          class: 'nav-item-to-left',
        },
        {
          default: () => navItemLeftSlot(),
        },
      );
    },

    navItemRightContent(navItemRightSlot) {
      if (!this.hasValidSlot(navItemRightSlot)) {
        return null;
      }

      return h(
        'div',
        {
          class: ['nav-item-to-right', 'ml-auto'],
        },
        {
          default: () => navItemRightSlot(),
        },
      );
    },

    navItemText(navItemDefaultSlot, navItemRightSlot) {
      const { text } = this;

      if (!text && this.hasValidSlot(navItemDefaultSlot)) {
        return navItemDefaultSlot();
      }

      const hasRightSlot = this.hasValidSlot(navItemRightSlot);

      return h(
        'span',
        {
          class: [{
            'nav-item-text': true,
            'mr-1': hasRightSlot || this.hasSubNav,
          }],
        },
        {
          default: () => text,
        },
      );
    },

    navItemSubNavCaret() {
      const { hasSubNav, subNavIsOpen, subNavAlwaysOpened } = this;

      if (!hasSubNav || subNavAlwaysOpened) {
        return false;
      }

      const iconCaretClasses = [{
        'm-0': true,
        'nav-item-to-right': true,
        'flip-vertical': subNavIsOpen,
      }];

      return h(
        DpIconCaret,
        {
          class: iconCaretClasses,
          size: 12,
        },
      );
    },

    navItem(navItemDefaultSlot, navItemLeftSlot, navItemRightSlot) {
      const { $attrs, hasSubNav, active, disabled, href, linkTag, to } = this;
      const { onClick } = $attrs;
      const content = [];

      const clickHandler = (event) => {
        if (this.shouldPreventDefault) {
          event.preventDefault();
        }

        if (disabled) {
          return;
        }

        if (hasSubNav) {
          this.subNavIsOpen = !this.subNavIsOpen;
        }

        if (typeof onClick === 'function') {
          onClick(event);
        }
      };

      const attrs = {
        ...$attrs,
        onClick: clickHandler,
      };

      if (!this.to) {
        attrs.href = href;
      }

      const slotsContent = [
        this.navItemLeftContent(navItemLeftSlot),
        this.navItemText(navItemDefaultSlot, navItemRightSlot),
        this.navItemRightContent(navItemRightSlot),
        this.navItemSubNavCaret(),
      ].filter(Boolean);

      return h(
        linkTag,
        {
          ...attrs,
          to,
          class: [{
            disabled,
            active,
            'nav-link': true,
          }],
        },
        {
          default: () => ([
            ...content,
            ...slotsContent,
          ]),
        },
      );
    },

    subNav(subNavContent) {
      if (!this.hasSubNav) {
        return false;
      }

      if (this.subNavAlwaysOpened) {
        this.subNavIsOpen = true;
      }

      return [h(
        'ul',
        {
          class: 'nav',
        },
        {
          default: () => subNavContent(),
        },
      )];
    },
  },

  render() {
    const { iconOffset, subNavIsOpen } = this;

    /* @slot Default slot for nav item (typically simple text) */
    const navItemDefaultSlot = this.$slots.default;

    /* @slot Slot for content placed from the left side of text (typically icon) */
    const navItemLeftSlot = this.$slots.contentLeft;

    /* @slot Slot for content placed from the right side of text (typically icon or label) */
    const navItemRightSlot = this.$slots.contentRight;

    /* @slot Use this slot for sub navigation nav items */
    const navItemSubNavSlot = this.$slots.subNav;

    const hasSubNav = this.hasValidSlot(navItemSubNavSlot);

    if (hasSubNav) {
      this.hasSubNav = true;
    }

    const children = [
      this.navItem(navItemDefaultSlot, navItemLeftSlot, navItemRightSlot),
      this.subNav(navItemSubNavSlot),
    ].filter(Boolean);

    return h(
      'li',
      {
        role: 'presentation',
        class: [{
          'nav-item': true,
          'nav-item-icon-offset': iconOffset,
          'nav-sub': hasSubNav,
          'nav-sub-show': subNavIsOpen,
        }],
      },
      {
        default: () => children,
      },
    );
  },
};
</script>
